import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Tooltip,
  Typography, 
} from "@mui/material";
import { breakpoints } from "../../theme/theme";
import { useGridApiRef } from '@mui/x-data-grid';
import { useLocation, useNavigate , Link } from "react-router-dom";
import {
  PlusIcon,
  TrashIcon,
  DocumentDuplicateIcon,
  ArrowsRightLeftIcon,
  ArrowDownTrayIcon,
  PlayIcon,
  PencilIcon,
  ArrowPathIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import styles from '../../styles/component.module.scss';
import dayjs from 'dayjs';
import isToday from 'dayjs/plugin/isToday';
import { createSingleSelectColumn } from '../../utils/filterOperators';

dayjs.extend(isToday);

// utilities
import { useSnackbar } from "../../utils/useSnackbar";
import CustomSnackbar from "../snackbar/CustomSnackbar";
import { TestCaseTypes, PriorityTypes, StatusTypes, MissingData, AutomationStatusEnum } from "../../enums/enumRegistry";
import { stripHtmlTags } from '../../utils/string';
import { dateFormat, formatDate , renderDateCell, formatDateCell } from '../../utils/date';
import attachmentCallBackDelete from '../../utils/backendUtils/attachmentCallBackDelete';
import { updateColumnVisibility } from '../../features/filter/columnVisibilitySlice';
import { exportToExcel } from "../../utils/exportToExcel";
import { getBreadcrumbText } from "../../utils/string";
import TagsComponent from '../Tags';
import ChipComponent from '../chips/ChipComponent/ChipComponent';
import { getPriorityVariant, getTestCaseTypeVariant, getTestCaseStatusVariant, getStatusIcon, getAutomationStatusVariant } from '../chips/ChipComponent/chipVariants';
import { formatStatusKey } from '../TestLaunchDetail/statusColorPallete';
import { resetAttachments } from '../../features/attachment/attachmentsSlice';
import { 
  fetchTestCasesByModule,
  deleteTestCase as deleteTestCaseApi,
  cloneTestCase as cloneTestCaseApi,
  moveTestCase as moveTestCaseApi
} from '../../utils/api';
import { createTestCase as createTestCaseApi, updateTestCase as updateTestCaseApi } from '../../utils/api/testcase_api';
import { STATUS } from '../../utils/api/sharedfunction';
import { getDeleteModalTitle, getDeleteConfirmMessage } from '../../utils/uiText';

// Query system imports (Phase 8) - Now using local state instead of Redux
import { getQuerySchema, parseTextAPI, executeQueryWithAST } from '../../utils/api/query_api';
import { filterPillsToAST } from '../../utils/query/filterPillsToAST';
import { syncUIToAdvanced, syncAdvancedToUI, canTransitionMode } from '../../utils/query/modeSync';
import { astToText } from '../../utils/query/astToText';
import { useQueryState } from '../../hooks/useQueryState';
import { useResettablePagination } from '../../hooks/useResettablePagination';
import FilterBar from '../Query/FilterBar';

// components
import TestCaseAddAndEditView from "./TesCaseAddAndEditView";
// import TestCaseDetailsView from "./TestCaseDetailsView";
import ConfirmationModal from '../Modal/ConfirmationModal';
import CustomModal from '../Modal/CustomModal';
import {CloneOrMoveModal} from '../Modal/CloneOrMoveModal';
import AddTCToTestRunModal from "../Modal/AddTCToTestRunModal";
import LabelChipContainer from "../chips/LabelChipContainer";
import TableComponent from '../TableComponent';
import TestCaseEmptyScreen from './TestCaseEmptyScreen';
import { TEST_CASE_EMPTY_VARIANTS } from './testCaseEmptyScreen.constants';
import RowActionMenu from "../menu/RowActionMenu";
import AddTCToTestGroupModal from "../Modal/AddTCToTestGroupModal";
import { renderTableCell, debounce } from "../../utils/commonfunctions";
import TestCaseDetailsView from "./Popup/TestCaseDetailsView";
import DEFAULT_ROUTES from "../../constants/routes";
import { setActivePage } from "../../features/navigation/navigationSlice";

const convertToExcel = (rows) => {
  return rows.map(row => {
    const rawDescription = row.description_plain ?? row.description ?? null;
    let descriptionForExport =
      rawDescription != null ? stripHtmlTags(String(rawDescription)) : null;
    if (descriptionForExport !== null && descriptionForExport.trim() === '') {
      descriptionForExport = null;
    }
    return {
    Key: row.key || null,
    Title: row.title || null,
    Description: descriptionForExport,
    Type: row.type || null,
    Priority: row.priority || null,
    Labels: row.labels?.map(label => label.name).join(' , ') || null,
    Status: row.status || null,
    'Generated By': row.generated_by_user?.username || null,
    'Created At': row.created_at ? dateFormat(row.created_at) : null,
    'Updated At': row.updated_at ? dateFormat(row.updated_at) : null,
    'Last Executed': row.last_executed_date ? dateFormat(row.last_executed_date) : null,
    'Updated By': row.updated_by_user?.username || null,
    'Executed By': row.last_executed_by || null,
    'Estimated Duration': row.estimated_duration || null,
    'Module Path': row.module ? row.module.modulePath?.map(m => m.name).join(' > ') : null,
  };
  });
};

const snackbarConfig = {
  add: { message: 'Test case added successfully!', severity: 'success' },
  update: { message: 'Test case updated successfully!', severity: 'success' },
  delete: (count) => ({ message: (count > 1) ? `${count} Test case(s) deleted successfully!` : 'Test case deleted successfully!', severity: 'success' }),
  copy: { message: 'Test case ID copied to clipboard!', severity: 'success' },
  'error save': { message: 'Error in Saving Test Case!', severity: 'error' },
  'error delete': { message: 'Error in Deleting Test Case!', severity: 'error' },
  'error fetch': { message: 'Error fetching test cases!', severity: 'error' },
  export: { message: 'Test cases download started!', severity: 'success' },
  'error testdata json format': { message: 'Test data not in correct json format. Please validate and try again', severity: 'error' },
  'clone': { message: 'Test case cloned successfully!', severity: 'success' },
  'move': { message: 'Test case(s) moved successfully!', severity: 'success' },
  'error clone': { message: 'Error cloning test case!', severity: 'error' },
  'error move': { message: 'Error moving test cases!', severity: 'error' },
  'same-module-error': { message: 'Cannot clone/move to the same module!', severity: 'error' }
};

const isValidJSON = (data) => {
  try {
    JSON.parse(data);
    return true;
  } catch (e) {
    return false;
  }
};

const TestCaseList = ({ moduleId, modulePath, addNewTestCaseModuleCount, moveTestCaseModuleCount, deleteTestCaseModuleCount, refreshKey, fetchLabelsForImportExcel, onLabelCreated, modules = [], onQueryChange }) => {
  // State management
  const dispatch = useDispatch();
  const { showSnackbar } = useSnackbar();
  const apiRef = useGridApiRef();
  const auth = useSelector((state) => state.auth);
  const selectedProject = useSelector(state => state.projects.selectedProject);
  const columnVisibilityModel = useSelector(state => state.columnVisibility.columnVisibilityModel);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Query system state - Using local state with persistence instead of Redux
  const {
    mode,
    filterPills,
    quickFilter,
    inputText,
    sortField,
    sortOrder,
    setMode,
    setFilterPills,
    setQuickFilter,
    setInputText,
    setSort,
    clearFilters
  } = useQueryState(selectedProject?.id);

  // Local state for query system (not persisted)
  const [schema, setSchema] = useState(null);
  const [schemaLoading, setSchemaLoading] = useState(false);
  const [ast, setAST] = useState(null);
  const [isParsing, setIsParsing] = useState(false);
  const [parseError, setParseError] = useState(null);
  const [resultsLoading, setResultsLoading] = useState(false);
  
  const [queryReady, setQueryReady] = useState(false);
  const hydratedProjectIdRef = useRef(null);
  
  // Compute isComplex from AST when it changes
  const isComplex = useMemo(() => {
    if (!ast) return false;
    try {
      const { astToFilterPills } = require('../../utils/query/astToFilterPills');
      const { isComplex: computed } = astToFilterPills(ast);
      return computed;
    } catch (error) {
      console.error('Error computing isComplex:', error);
      return false;
    }
  }, [ast]);

  const [testCases, setTestCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedTestCase, setSelectedTestCase] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTestCase, setEditingTestCase] = useState(null);
  const [modalMessage, setModalMessage] = useState('');
  const [modalType, setModalType] = useState('');
  const [isCustomModalOpen, setCustomModalOpen] = useState(false);
  const [cloneModalOpen, setCloneModalOpen] = useState(false);
  const [moveModalOpen, setMoveModalOpen] = useState(false);
  const [selectedCases, setSelectedCases] = useState([]);
  const [actionType, setActionType] = useState(null);
  const [isAddingToTestRun, setIsAddingToTestRun] = useState(false)
  const [selectedRows, setSelectedRows] = useState([]);
  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState('success');
  // Testcase Details Dialog
  const [openDialog, setOpenDialog] = useState(false); // To control the dialog visibility
  const [selectedRowData, setSelectedRowData] = useState(null); // To store the clicked row data
  // Testcase Edit Dialog
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const isMultiAction = selectedRows.length > 0;
  const haveSelections = selectedRows.length === 0;
  const isAllTestcase = moduleId === "default";
  const [isAddingToTestGroup, setIsAddingToTestGroup] = useState(false);
  

  const { 
    newAttachments, 
    deletedAttachments 
    } = useSelector((state) => state.attachments
  );
  
  // Pagination state — snaps back to page 0 whenever the module or active
  // filter changes (both change which rows the server returns).
  const [paginationModel, setPaginationModel] = useResettablePagination({
    pageSize: 25,
    resetKey: `${moduleId}|${JSON.stringify(ast)}`,
  });
  // Legacy filter model (kept for backward compatibility during transition)
  const [filterModel, setFilterModel] = useState({
    items: [],
    quickFilterValues: []
  });
  
  // Sort state is now persisted via useQueryState - convert to sortModel format for MUI DataGrid
  // sortModel format: [{ field: string, sort: 'asc' | 'desc' }]
  const sortModel = useMemo(() => {
    if (sortField && sortOrder) {
      return [{
        field: sortField,
        sort: sortOrder
      }];
    }
    // When there is no persisted sort, let the grid start unsorted.
    // The backend still defaults to created_at DESC when sortField/sortOrder are falsy.
    return [];
  }, [sortField, sortOrder]);
  

  const prevRefreshKeyRef = useRef(refreshKey);
  const prevModuleIdRef = useRef(null);
  // On mount / project change: fetch schema → parse persisted query → set queryReady
  // This ensures fetchTestCases never runs with stale/unhydrated query state
  useEffect(() => {
    if (!selectedProject?.id) return;
    if (hydratedProjectIdRef.current === selectedProject.id) return;
    
    let cancelled = false;
    
    const hydrate = async () => {
      setQueryReady(false);
      setAST(null);
      setParseError(null);
      setQuickFilterInput(quickFilter || '');
      
      setSchemaLoading(true);
      let fetchedSchema = null;
      try {
        const result = await getQuerySchema('testcase', selectedProject.id);
        if (cancelled) return;
        if (result.status === STATUS.SUCCESS && result.data) {
          fetchedSchema = result.data;
          setSchema(fetchedSchema);
        } else {
          console.error('Failed to fetch schema:', result.error);
        }
      } catch (error) {
        if (cancelled) return;
        console.error('Error fetching schema:', error);
      } finally {
        if (!cancelled) setSchemaLoading(false);
      }
      
      if (cancelled) return;
      
      if (fetchedSchema) {
        if (mode === 'advanced' && inputText && inputText.trim()) {
          try {
            const parseResult = await parseTextAPI(selectedProject.id, inputText, fetchedSchema);
            if (cancelled) return;
            if (parseResult.status === STATUS.SUCCESS && parseResult.data?.ast) {
              setAST(parseResult.data.ast);
            }
          } catch (error) {
            if (cancelled) return;
            console.error('Error parsing persisted query on hydration:', error);
          }
        } else if (mode === 'ui') {
          const currentPills = filterPills || [];
          const currentQuickFilter = quickFilter || '';
          const computedAST = filterPillsToAST(currentPills, currentQuickFilter, fetchedSchema);
          if (!cancelled) {
            setAST(computedAST);
            filterPillsRef.current = currentPills;
          }
        }
      }
      
      if (cancelled) return;
      
      hydratedProjectIdRef.current = selectedProject.id;
      setQueryReady(true);
    };
    
    hydrate();
    
    return () => { cancelled = true; };
  }, [
    selectedProject?.id,
    mode,
    inputText,
    filterPills,
    quickFilter,
  ]);
  
  useEffect(() => {
    if (!selectedProject?.id) return;
    const refreshKeyChanged = refreshKey !== undefined && refreshKey !== prevRefreshKeyRef.current;
    
    if (refreshKeyChanged) {
      prevRefreshKeyRef.current = refreshKey;
      setSchemaLoading(true);
      getQuerySchema('testcase', selectedProject.id)
        .then(result => {
          if (result.status === STATUS.SUCCESS && result.data) {
            setSchema(result.data);
          } else {
            console.error('Failed to fetch schema:', result.error);
          }
        })
        .catch(error => {
          console.error('Error fetching schema:', error);
        })
        .finally(() => {
          setSchemaLoading(false);
        });
    }
    
    if (refreshKey !== undefined) {
      prevRefreshKeyRef.current = refreshKey;
    }
  }, [selectedProject?.id, refreshKey]);

  const getAllModuleIds = useCallback((modulesList) => {
    const ids = new Set();
    const traverse = (nodes) => {
      if (!Array.isArray(nodes)) return;
      nodes.forEach(node => {
        if (node.id) {
          ids.add(node.id);
        }
        if (node.children && node.children.length > 0) {
          traverse(node.children);
        }
      });
    };
    traverse(modulesList);
    return ids;
  }, []);

  const getModuleId = useCallback((value) => {
    return typeof value === 'object' && value !== null ? value.id : value;
  }, []);

  useEffect(() => {
    if (!filterPills.length || !schema) return;

    const validModuleIdsFromSchema = new Set();
    const moduleIdToModuleMap = new Map(); // Map for name lookup and updates
    
    const moduleField = schema.fields?.find(f => f.name === 'module');
    if (moduleField?.values) {
      moduleField.values.forEach(m => {
        if (m.id) {
          validModuleIdsFromSchema.add(m.id);
          moduleIdToModuleMap.set(m.id, m); // Store module object for name lookup
        }
      });
    }

    // Get valid module IDs from actual modules tree
    const validModuleIdsFromModules = getAllModuleIds(modules);

    // More lenient validation: module is valid if it exists in EITHER schema OR modules list
    // This prevents removing valid selections when data is still loading
    const validModuleIds = new Set([
      ...validModuleIdsFromSchema,
      ...validModuleIdsFromModules
    ]);

    // Only validate if we have at least some module data
    // If we have no module data at all, skip validation to avoid false positives
    if (validModuleIds.size === 0 && validModuleIdsFromSchema.size === 0 && validModuleIdsFromModules.size === 0) {
      return; // Can't validate - no module data available yet
    }

    // Check for invalid modules AND outdated module names
    let hasChanges = false;
    let hadInvalidModules = false;
    
    const cleanedPills = filterPills.map(pill => {
      if (pill.field !== 'module') return pill;
      
      let updatedPill = { ...pill };
      
      // Skip validation for pills with empty/null values (still being set up)
      const hasEmptyValues = Array.isArray(pill.values) 
        ? pill.values.length === 0 || pill.values.every(v => !getModuleId(v))
        : !getModuleId(pill.values);
      
      if (hasEmptyValues) {
        // Don't validate empty pills - they're still being set up
        return pill;
      }
      
      if (Array.isArray(pill.values)) {

        const updatedValues = pill.values
          .map(v => {
            const moduleId = getModuleId(v);
            
            // Skip empty/null values
            if (!moduleId) {
              return v;
            }
            
            // Only remove if module is definitely invalid (exists in neither schema nor modules)
            // This is more lenient - allows modules that exist in either source
            const isInvalid = validModuleIds.size > 0 && !validModuleIds.has(moduleId);
            
            if (isInvalid) {
              hasChanges = true;
              hadInvalidModules = true;
              return null;
            }
            
            // Update name if module is valid but name changed
            if (validModuleIds.has(moduleId)) {
              const currentModule = moduleIdToModuleMap.get(moduleId);
              if (currentModule) {
                const currentName = typeof v === 'object' && v !== null ? v.name : null;
                if (currentName !== currentModule.name) {
                  hasChanges = true;
                  return currentModule; // Return updated module object with new name
                }
              }
            }
            
            return v;
          })
          .filter(v => v !== null);
        
        // Only remove pill if it had values but now has none (definitely invalid)
        // Don't remove if it was already empty (still being set up)
        if (updatedValues.length === 0 && pill.values.length > 0) {
          hasChanges = true;
          hadInvalidModules = true;
          return null; // Remove pill if no valid values remain
        }
        
        updatedPill.values = updatedValues;
      } else {
        const moduleId = getModuleId(pill.values);
        
        // Skip empty/null values
        if (!moduleId) {
          return pill;
        }
        
        // Only remove if module is definitely invalid (exists in neither schema nor modules)
        const isInvalid = validModuleIds.size > 0 && !validModuleIds.has(moduleId);
        
        if (isInvalid) {
          hasChanges = true;
          hadInvalidModules = true;
          return null;
        }
        
        // Update name if module is valid but name changed
        if (validModuleIds.has(moduleId)) {
          const currentModule = moduleIdToModuleMap.get(moduleId);
          if (currentModule) {
            const currentName = typeof pill.values === 'object' && pill.values !== null 
              ? pill.values.name 
              : null;
            if (currentName !== currentModule.name) {
              hasChanges = true;
              updatedPill.values = currentModule; // Update with new module object
            }
          }
        }
      }
      
      return updatedPill;
    }).filter(pill => pill !== null);

    // Only update if there are actual changes
    if (hasChanges) {
      setFilterPills(cleanedPills);
      filterPillsRef.current = cleanedPills;
      
      // Rebuild AST with cleaned/updated pills
      const newAST = filterPillsToAST(cleanedPills, quickFilter, schema);
      setAST(newAST);
      
      // Only show snackbar for deletions, not renames
      if (hadInvalidModules) {
        showSnackbar(
          'Some modules in your filter were deleted. The module filters have been removed.',
          'warning'
        );
      }
    }
  }, [schema, filterPills, quickFilter, showSnackbar, modules, getAllModuleIds, getModuleId]);

  const breadcrumbItems = modulePath?.map(module => ({
    label: module.name
  })) || [];


  useEffect(() => {
    if (!onQueryChange || !schema) return;

    // Simple guard: Skip if filterPills reference hasn't settled
    // (cleanup is still processing - prevents queries with invalid/deleted modules)
    if (filterPillsRef.current !== filterPills) {
      return;
    }

    let currentAST = null;
    
    if (mode === 'advanced') {
      currentAST = ast;
    } else if (mode === 'ui') {
      currentAST = filterPillsToAST(filterPills, quickFilter, schema);
    }
    
    onQueryChange(currentAST);
  }, [ast, filterPills, quickFilter, mode, schema, onQueryChange]);

  // Data fetching - Reactive pattern: reads ast from state
  // Handlers only update state → useEffect reacts → calls fetchTestCases
  const fetchTestCases = async (page, pageSize) => {
    setLoading(true);
    setResultsLoading(true);
    setTestCases([]);
    setTotalRowCount(0);
    
    try {
      if (ast) {
        // We have an AST (from advanced parse, UI pills, or quickFilter) → use query system
        const result = await executeQueryWithAST(selectedProject.id, {
          ast,
          page: page + 1,
          pageSize: pageSize,
          sortField: sortField || 'created_at',
          sortOrder: sortOrder || 'desc',
          moduleId: moduleId,
          schema: schema
        });
        
        if (result.status === STATUS.SUCCESS && result.data) {
          setTestCases(result.data.rows || []);
          setTotalRowCount(result.data.pagination?.total || result.data.pagination?.totalItems || 0);
        } else {
          throw new Error(result.error?.message || result.error || 'Query execution failed');
        }
      } else {
        // No AST → use legacy API (no filters applied)
        const response = await fetchTestCasesByModule({
          moduleId: moduleId,
          projectId: selectedProject.id,
          page: page + 1,
          pageSize: pageSize,
          sortField: sortField || 'created_at',
          sortOrder: sortOrder || 'desc',
        });
    
        setTestCases(response.rows || []);
        setTotalRowCount(response.total || 0);
      }
    } catch (error) {
      const errorMessage = error?.error || error?.message || "Failed to fetch test cases";
      showSnackbar(errorMessage, "error");
      console.error('Error fetching test cases:', error);
    } finally {
      setLoading(false);
      setResultsLoading(false);
    }
  };
  
  // Local state for quick filter input (for immediate UI update)
  const [quickFilterInput, setQuickFilterInput] = useState(quickFilter || '');
  
  // Refs to store latest values for debounced function
  const filterPillsRef = useRef(filterPills);
  const schemaRef = useRef(schema);
  
  // Update refs when values change
  useEffect(() => {
    filterPillsRef.current = filterPills;
  }, [filterPills]);
  
  useEffect(() => {
    schemaRef.current = schema;
  }, [schema]);
  
  // Sync local input with state when it changes externally
  useEffect(() => {
    setQuickFilterInput(quickFilter || '');
  }, [quickFilter]);
  
  // Track the last rendered module so the empty-screen overlay can detect a
  // module transition (isModuleTransitioning). Pagination reset on module/filter
  // change is handled by useResettablePagination above, not here.
  useEffect(() => {
    prevModuleIdRef.current = moduleId;
  }, [moduleId]);

  // === MAIN DATA FETCHING EFFECT (Reactive) ===
  // For filter/sort/pagination changes, this is the primary trigger for fetchTestCases.
  // CRUD handlers (delete, create, clone, move) also call fetchTestCases directly for refresh.
  useEffect(() => {
    // Gate: don't fetch until query state is fully hydrated
    if (!queryReady) return;

    fetchTestCases(paginationModel.page, paginationModel.pageSize);
  }, [
    queryReady,
    paginationModel,
    moduleId,
    selectedProject.id,
    sortField,
    sortOrder,
    refreshKey,
    ast,          // Filter changes (pills, advanced parse, quickFilter) all update AST
    mode,         // Mode changes may need a refetch (different query interpretation)
    filterModel,  // Legacy MUI filter model
  ]);
  
  // Handle filter pill changes
  const handleFilterPillChange = (pills) => {
    setFilterPills(pills);
    // Update ref immediately
    filterPillsRef.current = pills;
    // Note: Query execution is handled by handleFilterApply when values change
    // This function only updates the local state
  };

  // Handler for filter apply (called from FilterPill when selection changes)
  // Only updates state — the main useEffect reacts and fetches data
  const handleFilterApply = () => {
    const currentPills = filterPillsRef.current || filterPills || [];
    const currentQuickFilter = quickFilter || '';
    
    // Filter out pills without values (except unary operators)
    const validPills = currentPills.filter(pill => {
      if (!pill.values || !Array.isArray(pill.values) || pill.values.length === 0) {
        const unaryOperators = ['isEmpty', 'isNotEmpty'];
        return unaryOperators.includes(pill.operator);
      }
      return true;
    });
    
    // Compute new AST and update state — useEffect will react and fetch
    const newAST = filterPillsToAST(validPills, currentQuickFilter, schema);
    setAST(newAST);
    setLoading(true);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  // Handler to clear all filters at once
  // Only updates state — the main useEffect reacts and fetches data
  const handleClearAll = () => {
    const fixedFilters = (filterPills || []).filter(p => p.isFixed || p.field === 'project_id');
    
    setFilterPills(fixedFilters);
    filterPillsRef.current = fixedFilters;
    setQuickFilter('');
    setQuickFilterInput('');
    
    // Compute new AST and update state — useEffect will react and fetch
    const newAST = filterPillsToAST(fixedFilters, '', schema);
    setAST(newAST);
    setLoading(true);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };
  
  // Debounced function to update quick filter
  // Updates quickFilter state + computes AST → useEffect reacts and fetches
  const debouncedSetQuickFilter = useRef(
    debounce((value) => {
      const currentPills = filterPillsRef.current || [];
      const currentSchema = schemaRef.current;
      
      setQuickFilter(value);
      const newAST = filterPillsToAST(currentPills, value, currentSchema);
      setAST(newAST);
      setPaginationModel(prev => ({ ...prev, page: 0 }));
    }, 500)
  ).current;
  
  // Handle quick filter changes (immediate UI update + debounced state update)
  const handleQuickFilterChange = (value) => {
    setQuickFilterInput(value);
    debouncedSetQuickFilter(value);
  };
  
  // Handle mode change with synchronization between UI and Advanced modes
  const handleModeChange = async (newMode) => {
    // Check if transition is allowed
    const { allowed, reason } = canTransitionMode(mode, newMode, isComplex);
    
    if (!allowed) {
      showSnackbar(reason || 'Mode transition not allowed', 'error');
      return;
    }
    
    // Clear parse error when switching modes
    setParseError(null);
    
    if (mode === 'ui' && newMode === 'advanced') {
      const currentPills = filterPillsRef.current || filterPills || [];
      const currentQuickFilter = quickFilter || '';
      
      const { ast: newAST, text } = syncUIToAdvanced(currentPills, currentQuickFilter, schema || null);
      
      setAST(newAST);
      setInputText(text);
      setMode(newMode);
    } else if (mode === 'advanced' && newMode === 'ui') {
      let astToUse = ast;
      
      if (!astToUse && inputText && inputText.trim()) {
        try {
          const parseResult = await parseTextAPI(selectedProject.id, inputText, schema);
          if (parseResult.status === STATUS.SUCCESS && parseResult.data?.ast) {
            astToUse = parseResult.data.ast;
            setAST(astToUse);
          } else {
            showSnackbar('Query text exists but cannot be converted to UI mode. Please clear the query first.', 'error');
            return;
          }
        } catch (error) {
          showSnackbar('Query text exists but cannot be converted to UI mode. Please clear the query first.', 'error');
          return;
        }
      }
      
      // If no AST and no text, return to empty UI state
      if (!astToUse) {
        setMode(newMode);
        setAST(null);
        setFilterPills([]);
        setQuickFilter('');
        setInputText('');
        return;
      }
      
      // Convert AST to filter pills
      // Pass schema to convert user IDs to user objects for display
      const { filterPills: newPills, quickFilter: newQuickFilter, isComplex: newIsComplex, warning } = syncAdvancedToUI(astToUse, schema || null);
      
      // If complex, stay in advanced mode but set isComplex flag
      if (newIsComplex) {
        // Stay in advanced mode - keep AST
        showSnackbar(warning || 'This query is too complex for UI mode. Please edit it in Advanced mode.', 'warning');
        // Update filter pills for display (even though we're staying in advanced)
        setFilterPills(newPills);
        setQuickFilter(newQuickFilter);
        // Don't change mode - stay in advanced
        return;
      } else {
        // Switch to UI mode - compute AST from pills (prevents flash of unfiltered data)
        setMode(newMode);
        setFilterPills(newPills);
        setQuickFilter(newQuickFilter);
        setInputText('');
        
        // Compute AST from the new pills so data stays filtered during transition
        const newAST = filterPillsToAST(newPills, newQuickFilter, schema || null);
        setAST(newAST);
        
        // Update refs to ensure they're in sync
        filterPillsRef.current = newPills;
      }
    } else {
      // Other transitions (staying in same mode, etc.)
      setMode(newMode);
    }
  };
  
  // Handle advanced query text change
  const handleTextChange = (text) => {
    setInputText(text);
    // Clear parse error when user starts typing
    setParseError(null);
    // DO NOT clear results here - only clear when query is actually executed
    // This prevents test cases from disappearing while user is typing
  };
  
  // Clear advanced query text and apply empty filter (show all)
  const handleClearAdvanced = () => {
    setInputText('');
    setParseError(null);
    setAST(null);
    setIsParsing(false);
    setLoading(true);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  // Handle parse and execute in advanced mode
  // Only parses text → sets AST → useEffect reacts and fetches
  const handleParseAndExecute = async () => {
    setParseError(null);
    setIsParsing(true);
    
    // If query is empty, clear AST (useEffect will fetch with no filter)
    if (!inputText.trim()) {
      setAST(null);
      setIsParsing(false);
      setLoading(true);
      setPaginationModel(prev => ({ ...prev, page: 0 }));
      return;
    }
    
    // Parse text to AST
    try {
      const parseResult = await parseTextAPI(selectedProject.id, inputText, schema);
      setIsParsing(false);
      
      if (parseResult.status === STATUS.SUCCESS && parseResult.data?.ast) {
        // Set AST → useEffect will react and fetch data
        setAST(parseResult.data.ast);
        setLoading(true);
        setPaginationModel(prev => ({ ...prev, page: 0 }));
      } else {
        const errorMsg = parseResult.error?.message || parseResult.error || 'Failed to parse query';
        setParseError({ message: errorMsg });
        showSnackbar(errorMsg, 'error');
      }
    } catch (error) {
      setIsParsing(false);
      const errorMsg = error?.message || 'Failed to parse query';
      setParseError({ message: errorMsg });
      showSnackbar(errorMsg, 'error');
    }
  };
  
  // Handle add filter pill
  const handleAddFilter = () => {
    // TODO: Open filter pill creation dialog
    // For now, just add a placeholder
    console.log('Add filter pill - dialog to be implemented');
  };


  // Handlers
  const handleSnackbar = (actionType, count = 1) => {
    const configValue = snackbarConfig[actionType];
    const config = (typeof configValue === 'function' ? configValue(count) : configValue) || { message: 'Action completed!', severity: 'info' };
    setMessage(config.message);
    setSeverity(config.severity);
    setSnackbarOpen(true);
  };

  // Function to reset row selection
  const resetRowSelection = () => setSelectedRows([]);

  // Function to handle deletion of a test case
  const handleDelete = (testCase) => {
    setSelectedTestCase(testCase);
    setDeleteModalOpen(true);
  };

  // Function to confirm deletion of a test case
  const confirmDelete = async () => {
    try {
      // API Call
      const testCaseToDelete = isMultiAction ? selectedRows : [selectedTestCase.id];
      await deleteTestCaseApi({ ids: testCaseToDelete });
      setDeleteModalOpen(false);
      setSelectedTestCase(null);
      deleteTestCaseModuleCount(moduleId, testCaseToDelete.length);
      handleSnackbar('delete', testCaseToDelete.length);
      fetchTestCases(paginationModel.page, paginationModel.pageSize);
    } catch (error) {
      showSnackbar(error.response?.data?.message || "Failed to delete test case", "error");
      console.error('Error deleting test case:', error);
    }
  };

  const handleAdd = () => {
    setEditingTestCase(null);
    setModalOpen(true);
  };

  const handleEdit = (testCase) => {
    setEditingTestCase(testCase);
    setModalOpen(true);
  };

  const handleClose = () => {
    attachmentCallBackDelete(newAttachments);
    fetchLabelsForImportExcel && fetchLabelsForImportExcel()
    setModalOpen(false);
    handleCloseDrawer()
  };

  
    // Function to handle modal confirm
  const handleModalConfirm = async (updatedTestCase, selectedFiles = []) => {
    try {

      if (!isValidJSON(updatedTestCase.testdata) && updatedTestCase.testdata) {
        handleSnackbar('error testdata json format');
        return;
      }

      let saved = false;

      if (editingTestCase) {
        // EDIT FLOW: Handle S3 attachments (same pattern as create)
        const testCaseToUpdate = {
          ...updatedTestCase,
          testdata: updatedTestCase.testdata ? JSON.parse(updatedTestCase.testdata) : null,
          updated_by: auth.user.id,
        };

        // UPDATE FLOW: Use JIRA-style direct S3 upload (always JSON now)
        const response = await updateTestCaseApi({ testCaseId: testCaseToUpdate.id, ...testCaseToUpdate });

        if (response.status === STATUS.SUCCESS) {
          setTestCases(prev =>
            prev.map(tc =>
              tc.id === response.data.id ? response.data : tc
            )
          );

          if (selectedRowData && selectedRowData.id === response.data.id) {
            setSelectedRowData(response.data);
          }
          handleSnackbar('update');
          saved = true;
        } else {
          showSnackbar(response.error || response.message || "Failed to update test case", "error");
        }
        } else {
          // CREATE FLOW: Use JIRA-style direct S3 upload
          const newTestCase = {
            ...updatedTestCase,
            module_id: moduleId,
            generated_by: auth.user.id,
            updated_by: auth.user.id,
            testdata: updatedTestCase.testdata ? JSON.parse(updatedTestCase.testdata) : null,
            // tempFileKeys are already included in updatedTestCase from TesCaseAddAndEditView
          };

          // Always use JSON flow now (files are uploaded directly to S3)
          const response = await createTestCaseApi(newTestCase);

          if (response.status === STATUS.SUCCESS) {
            setTestCases(prev => [...prev, response.data]);
            handleSnackbar('add');
            dispatch(resetAttachments());
            addNewTestCaseModuleCount(moduleId);
            saved = true;
          } else {
            showSnackbar(response.error || response.message || "Failed to create test case", "error");
          }
        }

      if (saved) {
        fetchTestCases(paginationModel.page, paginationModel.pageSize);
        setModalOpen(false);
        handleCloseDrawer();
      }
    } catch (error) {
      showSnackbar(error.response?.data?.message || error.message || "Failed to save test case", "error");
      console.error('Error saving test case:', error);
    }
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const testCaseId = searchParams.get("testCaseId");

    if (testCaseId) {
      setOpenDialog(true);
    } else {
      setOpenDialog(false);
    }
  }, [location.search]);

  const handleRowClick = (params) => {
    const testCaseId = params?.row?.id;
    if(!testCaseId) return;
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("testCaseId", testCaseId);
    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: true,
    });
  };

  const handleEditDialog = (data) => {
    // Close the TestCaseDetailsView modal first
    setOpenDialog(false);
    // Then open the TestCaseEditView modal
    setEditingTestCase(data);
    setModalOpen(true);
  };

  // Function to close the drawer
  const handleCloseDrawer = () => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete("testCaseId");

    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: true,
    });

  };

  const handleExport = async (selectedRowIds) => {
    setLoading(true);
    try {
      let testCasesForExport = [];
      
      if (selectedRowIds && selectedRowIds.length > 0) {
        const result = await executeQueryWithAST(selectedProject.id, {
          ast: ast,
          page: 1,
          pageSize: 10000,
          sortField: sortField || 'created_at',
          sortOrder: sortOrder || 'desc',
          moduleId: moduleId,
          schema: schema,
          selectedTestCaseIds: selectedRowIds // Filter to only selected rows
        });
        
        if (result.status === STATUS.SUCCESS && result.data) {
          testCasesForExport = result.data.rows || [];
        } else {
          throw new Error(result.error?.message || result.error || 'Query execution failed');
        }
      } else {
        if (ast) {
          const result = await executeQueryWithAST(selectedProject.id, {
            ast: ast,
            page: 1,
            pageSize: 10000,
            sortField: sortField || 'created_at',
            sortOrder: sortOrder || 'desc',
            moduleId: moduleId,
            schema: schema
          });
          
          if (result.status === STATUS.SUCCESS && result.data) {
            testCasesForExport = result.data.rows || [];
          } else {
            throw new Error(result.error?.message || result.error || 'Query execution failed');
          }
        } else {
          const response = await fetchTestCasesByModule({
            moduleId: moduleId,
            projectId: selectedProject.id,
            page: 1,
            pageSize: 10000,
            sortField: sortField || 'created_at',
            sortOrder: sortOrder || 'desc',
          });
          
          testCasesForExport = response.rows || [];
        }
      }
      
      const csvContent = convertToExcel(testCasesForExport);
      exportToExcel(csvContent);
      handleSnackbar('export'); 
      setLoading(false);
    } catch (error) {
      setLoading(false);
      showSnackbar("Failed to export test cases", "error");
      console.error('Error exporting test cases:', error);
    }
  };


  const handleColumnVisibilityModelChange = (newModel) => {
    Object.entries(newModel).forEach(([columnName, visibility]) => {
      dispatch(updateColumnVisibility({ columnName, visibility }));
    });
  };

  const handleSingleClone = (testCase) => {
    setSelectedCases([testCase]);
    setCloneModalOpen(true);
    setActionType('single');
  };
  
  const handleSingleMove = (testCase) => {
    setSelectedCases([testCase]);
    setMoveModalOpen(true);
    setActionType('single');
  };
  

  const handleBulkClone = (selectedRowIds) => {
    const cases = testCases.filter(tc => selectedRowIds.includes(tc.id));
    setSelectedCases(cases);
    setCloneModalOpen(true);
    setActionType('bulk');

  };

  const navigateToAutomationStudio  = (testCase) => {
    dispatch(setActivePage('Automation Studio'));
    navigate(
      `${DEFAULT_ROUTES.BASE_URL}${DEFAULT_ROUTES.PROJECT}/${selectedProject.id}${DEFAULT_ROUTES.AUTOMATION_STUDIO}/${testCase.id}`,
      { replace: true }
    );
  };

  const handleBulkMove = (selectedRowIds) => {

    const cases = testCases.filter(tc => selectedRowIds.includes(tc.id));

    setSelectedCases(cases);

    setMoveModalOpen(true);

    setActionType('bulk');

  };

  
  const handleCloneConfirm = async (targetModuleId) => {
    try {
      const payload = actionType === 'single' 
        ? { testCaseId: selectedCases[0]?.id }
        : { testCaseIds: selectedCases.map(tc => tc.id) };
  
      await cloneTestCaseApi({
        ...payload,
        targetModuleId
      });

      handleSnackbar('clone');
      addNewTestCaseModuleCount(targetModuleId, payload?.testCaseIds.length);
      resetRowSelection();
      fetchTestCases(paginationModel.page, paginationModel.pageSize);
    } catch (error) {
      showSnackbar(error.response?.data?.message || "Failed to clone test case", "error");
      console.error('Error cloning test case(s):', error);
    }
  };
  
  const handleMoveConfirm = async (targetModuleId) => {
    try {
      const payload = actionType === 'single'
        ? { testCaseId: selectedCases[0]?.id }
        : { testCaseIds: selectedCases?.map(tc => tc.id) || [] };
  
      await moveTestCaseApi({
        ...payload,
        targetModuleId
      });
      handleSnackbar('move');
      moveTestCaseModuleCount(moduleId, targetModuleId, payload?.testCaseIds.length);
      resetRowSelection();
      fetchTestCases(paginationModel.page, paginationModel.pageSize);
    } catch (error) {
      showSnackbar(error.response?.data?.message || "Failed to move test case", "error");
      console.error('Error moving test case(s):', error);
    }
  };

  const handleAddToTestRun = async (data) => {
    resetRowSelection();
    showSnackbar(data.message, 'success');
  }

  const handleSelectionChange = (selectionModel) => {
    setSelectedRows(selectionModel); // Update selected row IDs
  };

  // Row action buttons for test case row
  const rowActionButtons = [
    {
      name: 'Edit',
      tooltip: 'Edit',
      icon: <PencilIcon className="icon" />,
      action: (row) => handleEdit(row),
      disabled: false,
    },
    {
      name: 'Delete',
      tooltip: 'Delete',
      icon: <TrashIcon className="icon" />,
      action: (row) => handleDelete(row),
      disabled: false,
    },
    {
      name: 'Clone',
      tooltip: 'Clone',
      icon: <DocumentDuplicateIcon className="icon" />,
      action: (row) => handleSingleClone(row),
      disabled: false,
    },
    {
      name: 'Move',
      tooltip: 'Move',
      icon: <ArrowsRightLeftIcon className="icon" />,
      action: (row) => handleSingleMove(row),
      disabled: false,
    },
    {
      name: 'Automation Studio',
      tooltip: 'Automation Studio',
      icon: <ArrowPathIcon className="icon" />,
      action: (row) => navigateToAutomationStudio(row),
      disabled: false,
    },
  ];




  // Header action handlers
  const handleBulkDelete = () => {
    setDeleteModalOpen(true);
  };

  const handleAddToTestRunClick = () => {
    setIsAddingToTestRun(true)
  };

  const handleAddToTestGroup = (data) => {
    resetRowSelection();
    showSnackbar(data.message, 'success');
  };

  // Header action buttons
  const headerActionButtons = [
    {
      name: "Add",
      tooltip: isAllTestcase
        ? "Select Module To Add Test Case"
        : "Add New Test Case",
      icon: <PlusIcon className="icon" />,
      action: handleAdd,
      disabled: isAllTestcase || loading,
      //fullbutton: true,
    },
    {
      name: "Delete",
      tooltip: "Delete Selected Test Case(s)",
      icon: <TrashIcon className="icon" />,
      action: () => handleBulkDelete(),
      disabled: haveSelections || loading,
    },
    {
      name: "Clone",
      tooltip: "Clone Selected Test Case(s)",
      icon: <DocumentDuplicateIcon className="icon" />,
      action: () => handleBulkClone(Array.from(selectedRows.values())),
      disabled: haveSelections || loading,
    },
    {
      name: "Move",
      tooltip: "Move Selected Test Case(s)",
      icon: <ArrowsRightLeftIcon className="icon" />,
      action: () => handleBulkMove(Array.from(selectedRows.values())),
      disabled: haveSelections || loading,
    },
    {
      name: "Export",
      tooltip: "Export Selected Test Case(s)",
      icon: <ArrowDownTrayIcon className="icon" />,
      action: () => handleExport(Array.from(selectedRows.values())),
      disabled: testCases.length === 0 || loading,
      section: 'right',
      contained: false,
    },
    // {
    //   name: "Testrun",
    //   tooltip: "Add to Testrun",
    //   icon: <PlayIcon style={{ width: 18, height: 18 }} />,
    //   action: () => handleAddToTestRunClick(),
    //   disabled: isAllTestcase || loading,
    // },
    {
      name: "Add to Test Group",
      tooltip: "Add Selected Test Case(s) to Test Group",
      icon: <PlayIcon className="icon" />,
      action: () => setIsAddingToTestGroup(true),
      disabled: haveSelections || loading,
    },
  ];


  const columns = [
    {
      field: '__check__',
      hideable: false,
      sortable: false,
      filterable: false,
      width: 55,
      resizable: false,
      headerAlign: 'center',
      align: 'center',        
    },
    {
      field: 'key',
      headerName: 'Test Case ID',
      flex: 1,
      minWidth: 120,
      maxWidth: 140,
      renderCell: (params) => {
        const testCaseId = params?.row?.id;

        let testCaseUrl = "#";

        if (testCaseId) {
          const searchParams = new URLSearchParams(window.location.search);
          searchParams.set("testCaseId", testCaseId);
          testCaseUrl = `${window.location.pathname}?${searchParams.toString()}`;
        }
        return (
          renderTableCell(params.value, "link", {
            type: 'link',
            to: testCaseUrl,
          })
        )
      },
    },      
    {
      field: 'title', 
      headerName: 'Summary', 
      flex: 3,  
      minWidth: 400,
      renderCell: (params) => renderTableCell(params.value),
    },
    createSingleSelectColumn('type', 'Type', Object.values(TestCaseTypes), {
      minWidth: 160,  
      maxWidth: 200,
      flex: 1,
      renderCell: (params) => (
        <ChipComponent
          variant={getTestCaseTypeVariant(params.value)}
          size="small"
        >
          {params.value || MissingData.NONE}
        </ChipComponent>
      ),
    }),
    createSingleSelectColumn('priority', 'Priority', Object.values(PriorityTypes), {
      minWidth: 130,  
      maxWidth: 170,
      flex: 1,
      renderCell: (params) => (
        <ChipComponent
          variant={getPriorityVariant(params.value)}
          size="small"
        >
          {params.value || MissingData.NONE}
        </ChipComponent>
      ),
    }),
    createSingleSelectColumn('status', 'Status', Object.values(StatusTypes), {
      minWidth: 130,
      maxWidth: 150,
      flex: 1,
      renderCell: (params) => {
        const status = formatStatusKey(params.value);
        return (
          <ChipComponent
            variant={getTestCaseStatusVariant(params.value)}
            icon={getStatusIcon(params.value)}
            size="small"
          >
            {status || MissingData.NONE}
          </ChipComponent>
        );
      },
    }),
    {
      field: 'labels',
      headerName: 'Labels',
      minWidth: 350,
      maxWidth: 420,
      flex: 2,
      sortable: false, // Disabled: Association field (many-to-many) - sorting by labels is ambiguous
      renderCell: (params) => {
        const labels = params.value || [];  
    
        // If there are no labels, render a fallback message
        if (labels.length === 0) {
          return (
            <Tooltip title={MissingData.NO_DATA}>
              {MissingData.NONE}
            </Tooltip>
          );
        }
    
        // Calculate the available width (max width minus padding)
        const maxWidth = params.colDef.computedWidth - 15;
    
        // Use the renderLabelChips function to render the chips
        return (
          <LabelChipContainer labels={labels} columnWidth={maxWidth} />
        );
      }
    },
    {
      field: 'generated_by_user', 
      headerName: 'Generated By', 
      minWidth: 130,
      maxWidth: 170,
      flex: 1,
      renderCell: (params) => renderTableCell(params.value?.username),
    },
    {
      field: 'created_at',
      headerName: 'Created At',
      minWidth: 240,
      maxWidth: 260,
      flex: 1,
      type: "dateTime",
      valueFormatter: (params) => dateFormat(params?.value),
      renderCell: renderDateCell,
    },
    {
      field: 'updated_at',
      headerName: 'Updated At',
      minWidth: 240,
      maxWidth: 260,
      flex: 1,
      type: "dateTime",
      valueFormatter: (params) => dateFormat(params?.value),
      renderCell: renderDateCell,
    },
    {
      field: 'last_executed_date',
      headerName: 'Last Executed',
      minWidth: 240,
      maxWidth: 260,
      flex: 1,
      type: "dateTime",
      filterable: false,
      valueFormatter: (params) => dateFormat(params?.value),
      renderCell: renderDateCell,
    },
    {
      field: "updated_by_user",
      headerName: "Updated By",
      minWidth: 130,
      maxWidth: 170,
      flex: 1,
      renderCell: (params) => renderTableCell(params.value?.username),
    },
    {
      field: "last_executed_by",
      headerName: "Executed By",
      minWidth: 130, 
      maxWidth: 170,
      filterable: false,
      flex: 1, 
      renderCell: (params) => renderTableCell(params.row.last_executed_by)
    },
    {
      field: 'estimated_duration',
      headerName: 'Estimated Time',
      minWidth: 150,
      maxWidth: 170,
      flex : 1,
      type: 'number',
      align: 'left', // We had to explicitly do this as in mui for number type, right is defualt
      headerAlign: 'left',
      renderCell: (params) => {
        const value = params.value || 15;
        return renderTableCell(`${value} minutes`)
      },
    },
    {
      field: 'module_path',
      headerName: 'Module Path',
      minWidth: 250,
      maxWidth: 300,
      flex: 2,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const module = params.row.module;
        if (!module) return <span>{MissingData.NONE}</span>;
        const pathString = module.modulePath?.map(m => m.name).join(' > ');
        return renderTableCell(pathString || module.name)
      },
    },
    createSingleSelectColumn('automation_status', 'Automation Status', Object.values(AutomationStatusEnum), {
      minWidth: 170,
      maxWidth: 180,
      flex: 1,
      renderCell: (params) => {
        // const status = formatStatusKey(params.value);
        return (
          <ChipComponent variant={getAutomationStatusVariant(params?.row?.automation_status)} size="small">
            {params?.row?.automation_status}
          </ChipComponent>
        )
        // return (
        //   <ChipComponent
        //     variant={getTestCaseStatusVariant(params.value)}
        //     icon={getStatusIcon(params.value)}
        //     size="small"
        //   >
        //     {status || MissingData.NONE}
        //   </ChipComponent>
        // );
      },
    }),
    // {
    //   field: 'automation_status',
    //   headerName: 'Automation Status',
    //   minWidth: 170,
    //   maxWidth: 180,
    //   flex : 1,
    //   headerAlign: 'left',
    //   renderCell: (params) => {
    //     return (
    //       <ChipComponent variant={getAutomationStatusVariant(params?.row?.automation_status)} size="small">
    //         {params?.row?.automation_status}
    //       </ChipComponent>
    //     )
    //   },
    // },
    {
      field: 'Actions',
      headerName: 'Actions',
      hideable: false,
      minWidth: 55,
      maxWidth: 90,
      resizable: false,
      disableColumnMenu: true,
      flex: 0,  
      headerAlign: 'center',
      sortable: false,
      align: 'center',
      filterable: false,
      renderCell: (params) => (
        <RowActionMenu
          menuItems={rowActionButtons}
          rowData={params.row}
          isDisabled={false}
        />
      ),
    },
  ];


  const isFilterActive = Boolean(ast)
    || (Array.isArray(filterPills) && filterPills.length > 0)
    || Boolean(quickFilter?.trim())
    || Boolean(inputText?.trim());
  const isDataLoading = loading || resultsLoading;
  const isModuleTransitioning = prevModuleIdRef.current !== null && prevModuleIdRef.current !== moduleId;
  const hasNoModules = modules.length === 0;
  const hasNoTestCases = !hasNoModules && !isAllTestcase && totalRowCount === 0 && !isFilterActive;
  const showNoModuleEmpty = hasNoModules;
  const showAllTestCasesEmpty = isAllTestcase && !showNoModuleEmpty;
  const showCustomNoData = !isFilterActive
    && !isDataLoading
    && !isModuleTransitioning
    && (showNoModuleEmpty || hasNoTestCases || showAllTestCasesEmpty);
  const emptyScreenVariant = showNoModuleEmpty
    ? TEST_CASE_EMPTY_VARIANTS.NO_MODULE
    : isAllTestcase
      ? TEST_CASE_EMPTY_VARIANTS.SELECT_MODULE
      : TEST_CASE_EMPTY_VARIANTS.NO_TEST_CASE;
  const selectedModuleName = modulePath?.[modulePath.length - 1]?.name;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/*  Query System Filter UI - Compact JIRA-like design */}
      <Box sx={{ flexShrink: 0, mb: 2 }}>
        <FilterBar
          filterPills={filterPills || []}
          quickFilter={quickFilterInput}
          inputText={inputText || ''}
          schema={schema}
          mode={mode || 'ui'}
          isComplex={isComplex || false}
          parseError={parseError}
          loading={resultsLoading}
          isParsing={isParsing}
          onFilterChange={handleFilterPillChange}
          onFilterApply={handleFilterApply}
          onClearAll={handleClearAll}
          onQuickFilterChange={handleQuickFilterChange}
          onTextChange={handleTextChange}
          onExecute={handleParseAndExecute}
          onClear={handleClearAdvanced}
          onAddFilter={handleAddFilter}
          onModeChange={handleModeChange}
        />
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
      <TableComponent
        viewType="testCase"
        title={getBreadcrumbText(breadcrumbItems) || "Test Cases"}
        showTitleTooltip={true}
        headerActionButtons={headerActionButtons}
        rows={testCases}
        columns={columns.filter(col => col.field !== 'module_path' || moduleId === "default")}
        handleRowClick={handleRowClick}
        isCheckbox={true}
        loading={loading || resultsLoading}
        idField="id"
        isColumnResizable={!loading}
        isPagination={true}
        isRowSelection={true}
        handleRowSelection={(selectionModel)=> setSelectedRows(selectionModel)}
        selectedRows={selectedRows}
        paginationMode="server"
        rowCount={totalRowCount}
        paginationModel={paginationModel}
        sortingMode="server"
        sortModel={sortModel}
        handleSortModelChange={(model) => {
          // Update local state (persisted via useQueryState) when sort changes.
          // When the grid clears sorting (empty model), clear persisted sort as well
          // so the grid can toggle between ASC/DESC/unsorted correctly.
          if (Array.isArray(model) && model.length > 0) {
            setSort({
              field: model[0].field,
              order: model[0].sort
            });
          } else {
            setSort({
              field: null,
              order: null
            });
          }
        }}
        handleCustomPaginationPageChange={(event, page)=> setPaginationModel({...paginationModel, page: page})}
        handleCustomPaginationPageSizeChange={(pageSize)=> setPaginationModel({page: 0, pageSize: pageSize})}
        isCustomPagination={true}
        disablePagination={false}
        disableFooter={false}
        getRowHeight={() => 'auto'}
        getEstimatedRowHeight={() => 100}
        customStyles={{
          // '& .MuiDataGrid-cell': {
          //   py: 1,
          //   display: 'flex',
          //   alignItems: 'center',
          //   paddingLeft: '18px',
          //   paddingRight: '18px',
          //   //height: '70px',
          // },
        }}
        apiRef={apiRef}
        hideColumns={['title']}
        isData={true}
        isVisibleInsideTableNoData={showCustomNoData}
        customNoData={(
          <TestCaseEmptyScreen
            variant={emptyScreenVariant}
            onAddTestCase={showNoModuleEmpty ? undefined : handleAdd}
            moduleName={selectedModuleName}
          />
        )}
      />
      </Box>

      {/* Modals and Snackbar */}
      <CustomSnackbar
        open={snackbarOpen}
        message={message}
        severity={severity}
        onClose={() => setSnackbarOpen(false)}
      />

      <ConfirmationModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        title={getDeleteModalTitle()}
        message={getDeleteConfirmMessage('test case', selectedRows.length, isMultiAction)}
      />

      <CustomModal
        isOpen={isCustomModalOpen}
        onClose={() => setCustomModalOpen(false)}
        type={modalType}
        message={modalMessage}
      />

      {/* For editing an existing test case */}
      <TestCaseAddAndEditView
        open={modalOpen}
        onClose={handleClose}
        onConfirm={handleModalConfirm}
        testCaseData={editingTestCase}
        modulePathForBreadcrumbs={modulePath}
        onLabelCreated={onLabelCreated}
      />

      {openDialog && (
        <TestCaseDetailsView
          open={openDialog}
          onClose={handleCloseDrawer}
          onEdit={handleEditDialog}
        />
      )}

      <CloneOrMoveModal
        open={cloneModalOpen}
        onClose={() => setCloneModalOpen(false)}
        onConfirm={handleCloneConfirm}
        action="clone"
        currentModuleId={moduleId}
        selectedCases={selectedCases} 
      />

      <CloneOrMoveModal
        open={moveModalOpen}
        onClose={() => setMoveModalOpen(false)}
        onConfirm={handleMoveConfirm}
        action="move"
        currentModuleId={moduleId}
        selectedCases={selectedCases} 
      />

      <AddTCToTestRunModal
          isOpen={isAddingToTestRun}
          onClose={() => setIsAddingToTestRun(false)}
          onAdd={handleAddToTestRun}
          onError={(errorMessage, errorType) => showSnackbar(errorMessage, errorType)}
          testCaseIds={selectedRows}
          modulePath={modulePath}
        />

      <AddTCToTestGroupModal
        isOpen={isAddingToTestGroup}
        onClose={() => setIsAddingToTestGroup(false)}
        onAdd={handleAddToTestGroup}
        onError={(errorMessage, errorType) => showSnackbar(errorMessage, errorType)}
        testCaseIds={selectedRows}
      />
    </Box>
  );
};

export default TestCaseList;
